import { and, Timestamp } from "@firebase/firestore";
import { notifications } from "@mantine/notifications";
import {
    addDoc,
    collection,
    collectionGroup,
    deleteDoc,
    doc,
    DocumentSnapshot,
    getDoc,
    getDocsFromCache,
    getDocsFromServer,
    query,
    Query,
    QuerySnapshot,
    setDoc,
    where,
    writeBatch,
} from 'firebase/firestore';
import { DateTime } from "luxon";
import { useContext, useEffect, useMemo, useState } from "react";
import { Suggestion } from "use-places-autocomplete";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { IconURLs, URLGroup, URLs } from '../components/PlaceSVGs';
import { useToday } from "../components/TripActivityScreen/dates";
import { AllTripActivitiesContext, HomeContext } from "./context";
import {
    FirebaseRefs,
    useFirebaseRefs,
    useMemoizedQuery,
} from "./firebase";
import {
    canNativelyGeocode,
    forwardGeocode,
    ForwardGeocodeResponse,
    forwardGeocodeViaNativeGeocoder,
} from "./geocoder";
import { getDetailsForPlaceId, getLocalityForPlaceId } from "./google";
import { fireMediumHaptic } from "./haptics";
import {
    MappableRecord,
    Place,
    PlaceClassType,
    placeConverter,
    PlaceRecord,
    PlaceType,
    SavedType,
    SearchEngineMappableRecord,
    SearchEngineResult,
} from "./interfaces";
import { getLogger } from "./logger";
import { Position, positionFromLatLng, PositionKey, positionToKey } from "./position";
import { isIdea, TSDate } from "./time";
import { UserData } from "./userdata";
import { MultiMap } from "../../shared/collections";

const logger = getLogger('placeFunctions');

export type AllTripActivities = {
    ideas: Place[] | undefined,
    past: Place[] | undefined,
    future: Place[] | undefined,
    today: Place[] | undefined,
    activityIdsByPositionKey: MultiMap<PositionKey, string>,
    activityIdsByPlaceId: MultiMap<string, string>,
    activityIdsByNarrowAddress: MultiMap<string, string>,
    allActivities: Map<string, Place>,
};

export const placeReverseSorter = (a: Place, b: Place) => (b.startdatetime?.daysSinceEpoch ?? 0) - (a.startdatetime?.daysSinceEpoch ?? 0);
export const placeForwardSorter = (a: Place, b: Place) => (a.startdatetime?.daysSinceEpoch ?? 0) - (b.startdatetime?.daysSinceEpoch ?? 0);

function classifyPlace(place: Place | undefined, beginningOfToday: TSDate) {
    if (!place) {
        return undefined;
    } else if (isIdea(place.startdatetime)) {
        return 'idea';
    } else if (place.startdatetime.isBefore(beginningOfToday)) {
        return 'past';
    } else if (place?.startdatetime.isAfter(beginningOfToday)) {
        return 'future';
    } else {
        return 'today';
    }
}

async function getRecentActivitiesFromCache(firebaseRefs: FirebaseRefs, firebaseUserId: string): Promise<Place[] | undefined> {
    const initialLookbackTimestamp = DateTime.now().minus({ days: 30 });
    const recentActivitiesQuery = query(
        collectionGroup(firebaseRefs.firestore, "activities"),
        and(
            where('acl.readWriteIds', 'array-contains', firebaseUserId),
            where('startdatetime', '>=', Timestamp.fromMillis(initialLookbackTimestamp.toMillis())),
        )
    )
        .withConverter(placeConverter);

    const querySnapshot = await getDocsFromCache(recentActivitiesQuery);
    return querySnapshot.docs
        .map(r => r.data())
        .filter(item => !!item);
}

function buildMapFromActivityList(candidates: Place[]) {
    const newMap = new Map<string, Place>();
    for (const record of candidates) {
        if (record.docid) {
            newMap.set(record.docid, record);
        }
    }
    return newMap;
}

export function useAllTripActivitiesContextState(userData: UserData): AllTripActivities {
    const firebaseRefs = useFirebaseRefs();
    const today = useToday();

    const [ allActivities, setAllActivities ] = useState<Map<string, Place> | undefined>();

    // Defer the unbounded collectionGroup('activities') live listener off the launch critical
    // path. On a cold relaunch into a restored trip route, this query's initial *server* sync
    // (every activity across every trip the user can access) otherwise monopolizes Firestore's
    // single work queue and delays the per-trip cache reads that gate the trip render — the
    // loader was observed dismissing only when this global sync completed. The cross-trip data
    // it feeds (coincident activities, idea/past/future buckets, the home widget) is not needed
    // for first paint, and getRecentActivitiesFromCache below still seeds recent activities from
    // cache immediately. We arm the listener once the app is idle after first paint.
    const [ liveQueryEnabled, setLiveQueryEnabled ] = useState(false);
    useEffect(() => {
        // requestIdleCallback waits for launch to settle; the setTimeout fallback is required on
        // iOS/WebKit (no requestIdleCallback) — the platform exhibiting the stall — and the
        // requestIdleCallback timeout likewise guarantees the listener eventually mounts.
        const w = window as unknown as {
            requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number,
            cancelIdleCallback?: (id: number) => void,
        };
        if (w.requestIdleCallback) {
            const id = w.requestIdleCallback(() => setLiveQueryEnabled(true), { timeout: 4000 });
            return () => w.cancelIdleCallback?.(id);
        }
        const id = setTimeout(() => setLiveQueryEnabled(true), 1500);
        return () => clearTimeout(id);
    }, []);

    const memoizedQuery = useMemo(
        () => liveQueryEnabled && userData.firebaseUserId
            ? query(
                collectionGroup(firebaseRefs.firestore, "activities"),
                where('acl.readWriteIds', 'array-contains', userData.firebaseUserId)
            ).withConverter(placeConverter)
            : undefined,
        [ firebaseRefs.firestore, userData.firebaseUserId, liveQueryEnabled ]
    );
    const activitiesFromFullDatabaseQuery = useMemoizedQuery(
        memoizedQuery,
        'useAllTripActivities',
    );

    useEffect(() => {
        if (activitiesFromFullDatabaseQuery) {
            setAllActivities(buildMapFromActivityList(activitiesFromFullDatabaseQuery ?? []));
        }
    }, [ activitiesFromFullDatabaseQuery ]);

    useEffect(() => {
        if (userData.firebaseUserId) {
            getRecentActivitiesFromCache(firebaseRefs, userData.firebaseUserId)
                .then(activities => {
                    if (activities && activities.length > 0) {
                        // Only bother with cached data if there's data to be had
                        setAllActivities(current => {
                            if (current) {
                                return current;
                            } else {
                                // only assign these cache values if we haven't already loaded something from the proper query
                                return buildMapFromActivityList(activities);
                            }
                        });
                    }
                });
        }
    }, [ firebaseRefs, userData.firebaseUserId ]);

    const separated = useMemo(
        () => allActivities
            ? separateActivitiesByTimePeriod(today, [ ...allActivities.values() ])
            : { ideas: undefined, past: undefined, future: undefined, today: undefined },
        [ allActivities, today ]
    );

    const [ activityIdsByPositionKey, activityIdsByPlaceId, activityIdsByNarrowAddress ] = useMemo(
        () => {
            const byPositionKey = new MultiMap<PositionKey, string>();
            const byPlaceId = new MultiMap<string, string>();
            const byNarrowAddress = new MultiMap<string, string>();
            if (allActivities) {
                for (const activity of allActivities.values()) {
                    if (activity.docid) {
                        const position = positionFromLatLng(activity);
                        if (position) {
                            byPositionKey.set(positionToKey(position), activity.docid);
                        }
                        // Key on the Google place id, which uniquely identifies a place.
                        if (activity.placeid) {
                            byPlaceId.set(activity.placeid, activity.docid);
                        }
                        // Address is a fallback key, but only when it's specific enough to
                        // identify one place. A bare locality like "Anguilla" must not match.
                        const narrowAddress = narrowAddressKey(activity.address);
                        if (narrowAddress) {
                            byNarrowAddress.set(narrowAddress, activity.docid);
                        }
                    }
                }
            }

            return [ byPositionKey, byPlaceId, byNarrowAddress ];
        },
        [ allActivities ]
    );

    return useMemo(() => ({
        ideas: mapToList(separated.ideas, (a, b) => (a.dayorder ?? 0) - (b.dayorder ?? 0)),
        past: mapToList(separated.past, placeReverseSorter),
        future: mapToList(separated.future, placeForwardSorter),
        today: mapToList(separated.today, placeForwardSorter),
        activityIdsByPositionKey,
        activityIdsByPlaceId,
        activityIdsByNarrowAddress,
        allActivities: allActivities ?? new Map(),
    }), [ separated, activityIdsByPositionKey, activityIdsByPlaceId, activityIdsByNarrowAddress, allActivities ]);
}

// An address is "narrow enough" to identify a single place (rather than a whole city or
// locality) when it has at least two separators — i.e. three or more components, such as
// "<street>, <city> <postcode>, <country>". Bare "Anguilla" (0) or "Paris, France" (1) do
// not qualify. Separators are commas or newlines (some addresses are newline-delimited).
// Returns a normalized key (trimmed, lowercased) so the same place typed in different cases
// still matches, or undefined when the address is too coarse to use.
export function narrowAddressKey(address: string | null | undefined): string | undefined {
    if (!address) {
        return undefined;
    }
    const normalized = address.trim().toLowerCase()
        .replace(/\n+/g, ', ') // some addresses are newline-delimited; treat those as separators
        .replace(/[ \t]+/g, ' ');
    const separators = (normalized.match(/,/g) ?? []).length;
    return separators >= 2 ? normalized : undefined;
}

function mapToList<T>(map: Map<string, T> | undefined, sorter: (a: T, b: T) => number) {
    return map === undefined ? undefined : [ ...map.values() ].sort(sorter);
}

function separateActivitiesByTimePeriod(today: TSDate, allActivities: Place[] | undefined) {
    if (!allActivities) {
        return { ideas: undefined, past: undefined, future: undefined, today: undefined };
    } else {
        const ideas: Map<string, Place> = new Map();
        const past: Map<string, Place> = new Map();
        const futures: Map<string, Place> = new Map();
        const todaysActivities: Map<string, Place> = new Map();
        allActivities?.forEach(place => {
            if (place.docid) {
                switch (classifyPlace(place, today)) {
                    case 'idea':
                        ideas.set(place.docid, place);
                        break;
                    case 'past':
                        past.set(place.docid, place);
                        break;
                    case 'future':
                        futures.set(place.docid, place);
                        break;
                    case 'today':
                        todaysActivities.set(place.docid, place);
                        break;
                }
            }
        });
        return {
            ideas,
            past,
            future: futures,
            today: todaysActivities,
        };
    }
}

export type BatchQueryMetadata = { queryTarget: 'server' | 'cache' };
export function executeQueriesAgainstCacheThenServer<T, U>(
    queries: Query<T>[],
    queryMapper: (q: QuerySnapshot<T> | undefined) => U[],
    queryReducer: (results: U[][], metadata: BatchQueryMetadata) => unknown
) {
    let hasClientError = false;
    const cacheResults = queries.map(async q => {
        try {
            return queryMapper(await getDocsFromCache(q));
        } catch (error) {
            hasClientError = true;
            // TODO should we filter to code=permission-denied and re-throw otherwise?
            logger.warn(`Trapped a Firestore error when querying against cache in executeQueriesAgainstCacheThenServer(): ${error}`);
            return undefined;
        }
    });

    Promise.all(cacheResults as Promise<U[]>[])
        .then(async awaited => {
            if (!hasClientError) {
                queryReducer(awaited, { queryTarget: 'cache' });

                // It is important that this only happens after we have received the full cached results.
                // Firebase has a weird behavior in which concurrent issuance of the same query results in
                // a cancellation of the first query processing step, but not until after the first chunk
                // (roughly 100 elements) is enlisted in cache. The first set of results are apparently
                // never passed to the handler function, but then the second query's handler function is
                // invoked with just that first chunk of data, and the full results are never fetched. The
                // second query's metadata indicates that the data is served from cache, but since the
                // cache wasn't fully populated, this is not the complete data set.
                let hasServerError = false;
                const serverResults = queries.map(async q => {
                    try {
                        return queryMapper(await getDocsFromServer(q));
                    } catch (error) {
                        hasServerError = true;
                        logger.warn(`Trapped a Firestore error when querying against server in executeQueriesAgainstCacheThenServer(): name=${error}`);
                        return undefined;
                    }
                });
                const awaitedServerResults = await Promise.all(serverResults);
                if (!hasServerError) {
                    const filteredResults = awaitedServerResults.filter(result => !!result) as U[][];
                    queryReducer(filteredResults, { queryTarget: 'server' });
                }
            }
        });
}

/**
 * Return the title if set; otherwise the 'location'.
 */
export function headlineForPlace(place: Place): string {
    return place.title ?? place.location ?? '<unknown>';
}

/**
 * Returns the address for the given place, excluding information presented via
 * {@link headlineForPlace} but including as much non-duplicate info as possible.
 */
export function addressForPlace(place: Place): string {
    if (place.title) {
        if (place.title === place.location) {
            return place.address ?? '';
        } else {
            return place.location && place.address
                ? `${place.location}, ${place.address}`
                : (place.address ?? place.location ?? '');
        }
    } else {
        // If there's no title, we used the location in the headline; don't repeat it here
        return place.address ?? '';
    }
}

export type PositionedPlace = Place & Position;

export function hasPosition(place: Place): place is PositionedPlace {
    return place.lat !== null && place.lat !== undefined
        && place.lng !== null && place.lng !== undefined;
}

export function removeUnpositioned(places: Place[]): PositionedPlace[] {
    return places.filter(place => hasPosition(place)) as PositionedPlace[];
}

export function placeToMappableRecord(place: PositionedPlace, isOverlay = false): PlaceRecord & MappableRecord {
    if (!place.docid) {
        throw Error("'place.docid' must be non-null!");
    }

    return {
        type: 'place',
        id: place.docid,
        place,
        isOverlay,
        title: place.title ?? undefined,
        location: place.location ?? undefined,
        address: async () => place.address ?? undefined,
        position: { lat: place.lat, lng: place.lng },
        locality: async () => place.locality ?? undefined,
    };
}

export async function searchEngineResultToMappableRecord(result: SearchEngineResult): Promise<SearchEngineMappableRecord | undefined> {
    const position = await result.position();
    if (position) {
        return {
            type: 'search-engine-result',
            searchEngineResult: result,
            // Apple results don't have IDs, but always(?) return addresses
            id: result.type === 'google-search' ? result.value.place_id : (await result.full_address())!,
            title: result.title,
            location: result.title,
            address: result.full_address,
            position,
            locality: result.locality,
        };
    } else {
        return undefined;
    }
}

export function navigateToPlace(router: AppRouterInstance, place: Place) {
    switch (place.placeclass) {
        case PlaceClassType.Activity:
            router.push(`/trip/${place.tripdocid}/${place.docid}`);
            break;
        case PlaceClassType.Saved:
            router.push(`/traveler/${place.creatorId}/saved/${place.docid}`);
            break;
        case PlaceClassType.Guide:
            router.push(`/traveler/${place.creatorId}/guide/${place.guideId}?seg=Map&place=${place.docid}`);
            break;
        default:
            console.warn(`Attempted to load a non-activity / non-saved-place record! `
                + `tripdocid: ${place.tripdocid}; docid: ${place.docid}; class: ${place.placeclass}`);
            break;
    }
}

export function extractPlaceTypeFromGoogleSuggestion(suggestion: Suggestion | undefined): PlaceType | undefined {
    if (!suggestion) {
        return undefined;
    } else if (suggestion.types.includes('restaurant')) {
        return PlaceType.Meal;
    } else {
        return undefined;
    }
}

export function assertNonEmpty(str: string | undefined | null, name: string) {
    // TODO should we do any other validation, like ensuring the field isn't a relative path? What sorts of
    //  path injection attacks are possible?
    if (!str || str.length === 0) {
        throw Error(`Field ${name} must not be empty (or null)!`);
    } else {
        return str;
    }
}

/**
 * Updates a trip activity with the partial values provided, merging with whatever's in the database.
 * The partial fields must not include 'placetype', as that is a saved-place-only field.
 */
export async function updateTripActivity(
    firebaseRefs: FirebaseRefs,
    tripId: string,
    placeDocId: string,
    placeFields: Omit<Partial<Place>, 'placetype'>
) {
    assertNonEmpty(tripId, 'tripId');
    assertNonEmpty(placeDocId, 'placeDocId');

    const ref = doc(firebaseRefs.firestore, 'trips', tripId, 'activities', placeDocId)
        .withConverter(placeConverter);
    await setDoc(ref, placeFields, { merge: true });
}

/**
 * Inserts a trip activity with the partial values provided.
 * The partial fields must not include 'placetype', as that is a saved-place-only field.
 */
export async function insertTripActivity(
    firebaseRefs: FirebaseRefs,
    tripId: string,
    userData: UserData,
    placeFields: Omit<Partial<Place>, 'placetype'>,
    additionalAction: (insertedDocId: string) => Promise<unknown>
) {
    assertNonEmpty(tripId, 'tripId');

    const place: Partial<Place> = {
        ...placeFields,
        tripdocid: tripId,
        creatorId: userData.firebaseUserId,
        created: DateTime.now(),
        schemaVersion: 1,
        activityType: placeFields.activityType ?? (placeFields as Place).placetype,
    };
    deleteField(place, 'placetype');

    const ref = doc(collection(firebaseRefs.firestore, 'trips', tripId, 'activities')
        .withConverter(placeConverter));
    return Promise.all([
        setDoc(ref, place),
        additionalAction(ref.id),
    ]);
}

function deleteField<T>(ob: T, field: keyof (T)) {
    if (Object.prototype.hasOwnProperty.call(ob, field)) {
        delete ob[field];
    }
}

// delete Activity in firestore
export async function deletePlace(firebaseRefs: FirebaseRefs, place: Place) {
    if (place.placeclass === PlaceClassType.Activity) {
        if (place.tripdocid && place.docid) {
            fireMediumHaptic();
            const fref = doc(
                firebaseRefs.firestore,
                'trips',
                place.tripdocid,
                'activities',
                place.docid
            );
            deleteDoc(fref);

            notifications.show({
                title: 'Activity Deleted',
                message: 'Your activity is deleted',
                autoClose: true,
            });
        } else {
            notifications.show({
                title: 'Failed to delete',
                message: 'Your activity was not deleted',
                autoClose: false,
            });
            logger.warn(
                `Failed to delete activity! docid='${place.docid}', tripdocid='${place.tripdocid}'`
            );
        }
    } else if (place.placeclass === PlaceClassType.Guide) {
        const userId = place.creatorId ?? place.userid;
        if (userId && place.docid) {
            fireMediumHaptic();
            const guideId = assertNonEmpty(place.guideId, 'place.guideId');
            const fref = doc(
                firebaseRefs.firestore,
                'users',
                userId,
                'guides',
                guideId,
                'guideplaces',
                place.docid
            );

            return deleteDoc(fref)
                .then(() =>
                    notifications.show({
                        title: 'Guide Place Deleted',
                        message: 'Your guide place was deleted',
                        autoClose: true,
                    })
                )
                .catch((error) => {
                    notifications.show({
                        title: 'Delete Failed',
                        message: 'Could not delete the guide place. Please try again.',
                        autoClose: true,
                        color: 'red',
                    });
                    throw error;
                });
        }
    } else {
        const userId = place.creatorId ?? place.userid;
        if (userId && place.docid) {
            fireMediumHaptic();
            const fref = doc(firebaseRefs.firestore, 'users', userId, 'places', place.docid);
            deleteDoc(fref);

            notifications.show({
                title: 'Saved Place Deleted',
                message: 'Your saved place is deleted',
                autoClose: true,
            });
        } else {
            notifications.show({
                title: 'Failed to delete',
                message: 'Your saved place was not deleted',
                autoClose: false,
            });
            logger.warn(
                `Failed to delete saved place! docid='${place.docid}', creatorId='${place.creatorId}', userid='${place.userid}'`
            );
        }
    }
}

/**
 * Inserts a saved place with the partial values provided.
 */
export async function insertSavedPlace(
    firebaseRefs: FirebaseRefs,
    userData: UserData,
    placeFields: Partial<Place>,
    category?: SavedType,
) {
    const userId = assertNonEmpty(userData.firebaseUserId, 'userData.firebaseUserId');

    const ref = collection(firebaseRefs.firestore, 'users', userId, 'places')
        .withConverter(placeConverter);
    const categories: SavedType[] = [];
    if (category) {
        categories.push(category);
    }
    categories.push(...(placeFields.categories ?? []).filter(c => c !== category));

    const place = {
        ...placeFields,
        userid: userId,
        schemaVersion: 1,
        categories,
        placetype: placeFields.activityType ?? placeFields.placetype,
    } as Partial<Place>;

    // Remove some fields that don't belong in a saved place, or shouldn't be copied
    deleteField(place, 'docid');
    deleteField(place, 'created');
    deleteField(place, 'updatedAt');
    deleteField(place, 'order');
    deleteField(place, 'activityType');
    deleteField(place, 'creatorId');
    deleteField(place, 'tripdocid');
    deleteField(place, 'startdatetime');
    deleteField(place, 'enddatetime');
    deleteField(place, 'dayorder');
    deleteField(place, 'totalvotes');

    if (!place.location) {
        place.location = place.title;
    }

    await addDoc(ref, place);
}

/**
 * Inserts a guide place with the partial values provided.
 * Returns the auto-generated Firestore document ID.
 */
export async function insertGuidePlace(
    firebaseRefs: FirebaseRefs,
    userData: UserData,
    guideDocId: string | null | undefined,
    placeFields: Partial<Place>,
): Promise<string> {
    const userId = assertNonEmpty(userData.firebaseUserId, 'userData.firebaseUserId');
    const guideId = assertNonEmpty(guideDocId, 'guideDocId');

    const ref = collection(firebaseRefs.firestore, 'users', userId, 'guides', guideId, 'guideplaces')
        .withConverter(placeConverter);

    const place = {
        ...placeFields,
        userid: userId,
        schemaVersion: 1,
        guideId,
        created: DateTime.now(),
        placetype: placeFields.activityType ?? placeFields.placetype,
    } as Partial<Place>;

    // Remove some fields that don't belong in a guide place, or shouldn't be copied
    deleteField(place, 'docid');
    deleteField(place, 'order');
    deleteField(place, 'activityType');
    deleteField(place, 'categories');
    deleteField(place, 'dayorder');
    deleteField(place, 'enddatetime');
    deleteField(place, 'startdatetime');
    deleteField(place, 'tripdocid');
    delete (place as any).acl;
    delete (place as any).category;



    if (!place.location) {
        place.location = place.title;
    }

    const docRef = await addDoc(ref, place);
    return docRef.id;
}

/**
 * Updates a saved place with the partial values provided, merging with whatever's in the database.
 * The partial fields must not include 'activityType', as that is a trip-activity-only field.
 */
export async function updateSavedPlace(
    firebaseRefs: FirebaseRefs,
    userData: UserData,
    placeDocId: string,
    placeFields: Omit<Partial<Place>, 'activityType'>) {
    const userId = assertNonEmpty(userData.firebaseUserId, 'userData.firebaseUserId');
    assertNonEmpty(placeDocId, 'placeDocId');

    deleteField(placeFields as Place, 'activityType');

    const ref = doc(firebaseRefs.firestore, 'users', userId, 'places', placeDocId)
        .withConverter(placeConverter);
    await setDoc(ref, placeFields, { merge: true });
}

/**
 * Updates a guide place with the partial values provided, merging with whatever's in the database.
 * The partial fields must not include 'activityType', as that is a trip-activity-only field.
 */
export async function updateGuidePlace(
    firebaseRefs: FirebaseRefs,
    userData: UserData,
    guideDocId: string,
    placeDocId: string,
    placeFields: Omit<Partial<Place>, 'activityType'>
) {
    const userId = assertNonEmpty(userData.firebaseUserId, 'userData.firebaseUserId');
    const guideId = assertNonEmpty(guideDocId, 'guideDocId');
    assertNonEmpty(placeDocId, 'placeDocId');

    // 2. Build a clean copy for the guide subcollection
    const guidePlaceCopy = {
        ...placeFields,
        guideId,
        placetype: placeFields.placetype,
        schemaVersion: 1,
        updatedAt: DateTime.now(),
    } as Partial<Place>;

    // Remove some fields that don't belong in a guide place, or shouldn't be copied
    deleteField(guidePlaceCopy, 'docid');
    deleteField(guidePlaceCopy, 'activityType');
    deleteField(guidePlaceCopy, 'categories');
    deleteField(guidePlaceCopy, 'dayorder');
    deleteField(guidePlaceCopy, 'enddatetime');
    deleteField(guidePlaceCopy, 'startdatetime');
    deleteField(guidePlaceCopy, 'tripdocid');
    delete (guidePlaceCopy as any).acl;
    delete (guidePlaceCopy as any).category;

    const ref = doc(
        firebaseRefs.firestore,
        'users',
        userId,
        'guides',
        guideId,
        'guideplaces',
        placeDocId
    ).withConverter(placeConverter);
    await setDoc(ref, guidePlaceCopy, { merge: true });
}

export async function addGuidePlaceDocs(
    firebaseRefs: FirebaseRefs,
    guideId: string,
    userId: string,
    placeDocidsToAdd: string[],
    fallbackPlaceData?: ReadonlyMap<string, Place>,
) {
    if (placeDocidsToAdd.length === 0) return;

    const MAX_BATCH_SIZE = 500;
    const READ_CONCURRENCY = 50; // New constant to limit concurrent reads

    for (let i = 0; i < placeDocidsToAdd.length; i += MAX_BATCH_SIZE) {
        const placeDocidChunk = placeDocidsToAdd.slice(i, i + MAX_BATCH_SIZE);
        const sourceRefs = placeDocidChunk.map((placeDocid) =>
            doc(
                firebaseRefs.firestore,
                'users',
                userId,
                'places',
                placeDocid
            ).withConverter(placeConverter)
        );

        // Chunk the reads into smaller pools to avoid network/memory bursts
        const snaps = [];
        for (let r = 0; r < sourceRefs.length; r += READ_CONCURRENCY) {
            const readChunk = sourceRefs.slice(r, r + READ_CONCURRENCY);
            const chunkSnaps = await Promise.all(readChunk.map((srcRef) => getDoc(srcRef)));
            snaps.push(...chunkSnaps);
        }

        const batch = writeBatch(firebaseRefs.firestore);
        for (let chunkIndex = 0; chunkIndex < placeDocidChunk.length; chunkIndex++) {
            const placeDocid = placeDocidChunk[chunkIndex];
            const snap = snaps[chunkIndex];

            // Determine source data: prefer saved-place snapshot, fall back to
            // cached guide-local place data (for places that only live in guideplaces).
            let placeData: Place | undefined;
            if (!snap.exists()) {
                placeData = fallbackPlaceData?.get(placeDocid);
                if (!placeData) {
                    console.warn(`addGuidePlaceDocs: place ${placeDocid} not found in saved places or fallback cache – skipping`);
                    continue;
                }
            } else {
                placeData = snap.data();
            }

            if (!placeData) {
                console.warn(`addGuidePlaceDocs: place ${placeDocid} returned undefined data – skipping`);
                continue;
            }

            // 2. Build a clean copy for the guide subcollection
            const guidePlaceCopy = {
                ...placeData,
                docid: placeDocid,
                guideId,
                placetype: placeData.placetype ?? placeData.activityType,
                // Preserve original creation time for guide-local places being restored;
                // use now() only for genuinely new additions from saved places.
                created: snap.exists() ? DateTime.now() : (placeData.created ?? DateTime.now()),
                schemaVersion: 1,
            } as Partial<Place>;
            // Remove fields that don't belong in a guide place or shouldn't be copied
            deleteField(guidePlaceCopy, 'activityType');
            deleteField(guidePlaceCopy, 'docid');
            deleteField(guidePlaceCopy, 'order');
            deleteField(guidePlaceCopy, 'activityType');
            deleteField(guidePlaceCopy, 'categories');
            deleteField(guidePlaceCopy, 'dayorder');
            deleteField(guidePlaceCopy, 'enddatetime');
            deleteField(guidePlaceCopy, 'startdatetime');
            deleteField(guidePlaceCopy, 'tripdocid');
            deleteField(guidePlaceCopy, 'updatedAt');
            deleteField(guidePlaceCopy, 'review');
            delete (guidePlaceCopy as any).acl;
            delete (guidePlaceCopy as any).category;
            // 3. Write the cleaned copy into the guide's guideplaces subcollection
            const destRef = doc(
                firebaseRefs.firestore,
                'users',
                userId,
                'guides',
                guideId,
                'guideplaces',
                placeDocid
            ).withConverter(placeConverter);
            batch.set(destRef, guidePlaceCopy as Place, { merge: true });
        }
        await batch.commit();
    }
}

/**
  * Reads the specified guideplaces documents and returns a Map of docid → Place.
  * Used to cache guide-local place data before removal so it can be restored on undo.
  */
export async function readGuidePlaceDocs(
    firebaseRefs: FirebaseRefs,
    guideId: string,
    userId: string,
    placeDocids: string[],
): Promise<Map<string, Place>> {
    if (placeDocids.length === 0) return new Map();
    const READ_CONCURRENCY = 50;

    const refs = placeDocids.map((id) =>
        doc(
            firebaseRefs.firestore,
            'users', userId, 'guides', guideId, 'guideplaces', id
        ).withConverter(placeConverter)
    );

    const snaps: DocumentSnapshot<Place>[] = [];
    for (let r = 0; r < refs.length; r += READ_CONCURRENCY) {
        const readChunk = refs.slice(r, r + READ_CONCURRENCY);
        const chunkSnaps = await Promise.all(readChunk.map((ref) => getDoc(ref)));
        snaps.push(...chunkSnaps);
    }

    const result = new Map<string, Place>();
    for (let i = 0; i < placeDocids.length; i++) {
        const snap = snaps[i];
        if (snap.exists()) {
            result.set(placeDocids[i], snap.data()!);
        }
    }
    return result;
}

export async function removeGuidePlaceDocs(
    firebaseRefs: FirebaseRefs,
    guideId: string,
    userId: string,
    placeDocidsToRemove: string[],
) {
    if (placeDocidsToRemove.length === 0) return;

    const maxBatchSize = 500;
    const commits: Promise<void>[] = [];
    for (let i = 0; i < placeDocidsToRemove.length; i += maxBatchSize) {
        const batch = writeBatch(firebaseRefs.firestore);
        const placeDocidsBatch = placeDocidsToRemove.slice(i, i + maxBatchSize);
        for (const placeDocid of placeDocidsBatch) {
            const ref = doc(
                firebaseRefs.firestore,
                'users',
                userId,
                'guides',
                guideId,
                'guideplaces',
                placeDocid
            );
            batch.delete(ref);
        }
        commits.push(batch.commit());
    }
    await Promise.all(commits);
}

export function mapIconUrlForPlace(place: Place, selfSavedCategories: SavedType[], activeTripOrGuideId: string | undefined) {
    let urlGroup: URLGroup;
    if (place.placeclass === PlaceClassType.Saved) {
        if (place.categories?.includes('Favorite')) {
            urlGroup = URLs.SavedFavorite;
        } else {
            urlGroup = URLs.SavedWantToGo;
        }
    } else if (place.placeclass === PlaceClassType.Guide) {
        urlGroup = URLs.GuidePlace;
    } else {
        const urlGroupGroup = activeTripOrGuideId === place.tripdocid ? URLs.ActiveTrip : URLs.OtherTrip;
        if (place.categories?.includes('Favorite') || selfSavedCategories.includes('Favorite')) {
            urlGroup = urlGroupGroup.TripAndFavorite;
        } else if (place.categories?.includes('Want to go') || selfSavedCategories.includes('Want to go')) {
            urlGroup = urlGroupGroup.TripAndWantToGo;
        } else {
            urlGroup = urlGroupGroup.TripActivity;
        }
    }

    const placeType = placeTypeFromPlace(place);
    let urls: IconURLs;
    switch (placeType) {
        case PlaceType.Hotel:
        case PlaceType.Lodging:
            urls = urlGroup.Lodging;
            break;
        case PlaceType.Meal:
            urls = urlGroup.Food;
            break;
        case PlaceType.Transit:
            urls = urlGroup.Flight;
            break;
        default:
            urls = urlGroup.BlueDot;
    }

    return urls.map;
}

/**
 * Who owns a place.
 *
 * `userid` is the legacy field and is null on anything written since `creatorId` was introduced (see
 * the converter's `creatorId: data.creatorId ?? data.userid`). Testing `place.userid` alone silently
 * reports "not yours" for most current records, so ownership checks must go through here.
 */
export function placeOwnerId(place: Pick<Place, 'creatorId' | 'userid'>): string | null {
    return place.creatorId ?? place.userid;
}

export function isOwnedBy(place: Pick<Place, 'creatorId' | 'userid'>, userId: string | undefined): boolean {
    return !!userId && placeOwnerId(place) === userId;
}

export function isLodging(place: Pick<Place, 'activityType'> | null): boolean {
    return place?.activityType === PlaceType.Lodging || place?.activityType === PlaceType.Hotel;
}

export function dateRange(place: Pick<Place, 'startdatetime' | 'enddatetime'>) {
    if (!place.startdatetime || isIdea(place.startdatetime)) {
        return [];
    } else if (!place.enddatetime || !place.startdatetime.isBefore(place.enddatetime)) {
        return [ place.startdatetime ];
    } else {
        const range: TSDate[] = [];
        for (let i = 0; !place.startdatetime.plus({ days: i })
            .isAfter(place.enddatetime); i++) {
            range.push(place.startdatetime.plus({ days: i }));
        }
        return range;
    }
}

export function placeTypeFromPlace(place: Partial<Place>): PlaceType | undefined {
    let placeType: PlaceType | undefined;
    if (place.placeclass === PlaceClassType.Saved) {
        placeType = place.placetype ?? place.activityType ?? PlaceType.Activity;
    } else if (place.placeclass === PlaceClassType.Guide) {
        placeType = place.placetype ?? place.activityType ?? PlaceType.Activity;
    } else if (place.placeclass === PlaceClassType.Activity) {
        placeType = place.activityType ?? place.placetype ?? PlaceType.Activity;
    } else {
        console.warn(`Unexpected placeclass: ${place.placeclass}. Will fall back to 'Activity'`);
        placeType = PlaceType.Activity;
    }

    if (placeType === PlaceType.Hotel) {
        return PlaceType.Lodging;
    } else {
        return placeType;
    }
}

// User-facing label for a place type. The Transit type's stored value is 'Flight' for back-compat,
// so anything that shows a type to the user must go through this rather than the raw value.
export function placeTypeLabel(type: PlaceType | null | undefined): string {
    return type === PlaceType.Transit ? 'Transit' : (type ?? '');
}

// Scan the activities for places that have a known address but no known
// coordinates, and geocode them using on-device capabilities
export function repairTripActivities(firebaseRefs: FirebaseRefs, activities: Place[]): () => unknown {
    const problemActivities = activities.filter(activity => {
        return (activity.lat === null || activity.lng === null)
            && (activity.address ?? '').trim() !== '';
    });

    logger.log(`Found ${problemActivities.length} activities in need of repair`);

    if (!canNativelyGeocode()) {
        return () => {};
    }

    let isRunning = true;
    setTimeout(async () => {
        for (const activity of problemActivities) {
            if (!isRunning) {
                break;
            }
            if (activity.tripdocid && activity.docid && activity.address) {
                // We explicitly use the native geocoder here, so that we never incur costs for this repair
                // eslint-disable-next-line no-await-in-loop
                const result = await forwardGeocodeViaNativeGeocoder(activity.address);
                if (result) {
                    const patch = {
                        lat: result.position?.lat ?? null,
                        lng: result.position?.lng ?? null,
                    };
                    updateTripActivity(firebaseRefs, activity.tripdocid, activity.docid, patch);
                }
            }
        }
    }, 0);

    return () => { isRunning = false; };
}

export async function geocodeAndUpdatePlace(
    originalFields: Partial<Place> | undefined,
    newFields: Partial<Place>) {
    return testableGeocodePlaceHelper(
        originalFields,
        newFields,
        forwardGeocode,
        getDetailsForPlaceId,
        getLocalityForPlaceId
    );
}

export async function testableGeocodePlaceHelper(
    originalFields: Partial<Place> | undefined,
    newFields: Partial<Place>,
    forwardGeocodeFn: (address: string) => Promise<ForwardGeocodeResponse | undefined>,
    getDetailsFn: (placeId: string) => Promise<{ position: Position | undefined, locality: string | undefined } | undefined>,
    getLocalityFn: (placeId: string) => Promise<string | undefined>
) {
    const hasNewAddress = originalFields?.address !== newFields.address;
    const hasNewLocality = originalFields?.locality !== newFields.locality;
    const hasNewPosition = originalFields?.lat !== newFields.lat || originalFields?.lng !== newFields.lng;
    if (hasNewAddress && hasNewLocality && hasNewPosition) {
        // if everything is new, assume the values were all properly set
        return;
    }

    const needsPosition = newFields.lat === null || newFields.lng === null;
    const needsLocality = !newFields.locality;
    if (hasNewAddress || (newFields.address && (needsPosition || needsLocality))) {
        // if we have a new address, geocode it and update the position and locality accordingly
        const newValue = newFields.address;
        if (newValue && newValue.trim() !== '') {
            try {
                const geocoded = await forwardGeocodeFn(newValue);
                if (needsPosition && geocoded?.position) {
                    newFields.lat = geocoded?.position?.lat ?? null;
                    newFields.lng = geocoded?.position?.lng ?? null;
                }
                if (needsLocality && geocoded?.locality) {
                    newFields.locality = geocoded?.locality;
                }
            } catch (err) {
                logger.warn(`Failed to geocode address! ${err}`);
                console.warn("Failed to geocode address!", err);
            }
        }
    }

    const stillNeedsPosition = newFields.lat === null || newFields.lng === null;
    const stillNeedsLocality = !newFields.locality;
    if (stillNeedsPosition || stillNeedsLocality) {
        try {
            if (newFields.placeid) {
                // do a lookup by place id. This is the most expensive option, so we do it last.
                if (stillNeedsPosition) {
                    const details = await getDetailsFn(newFields.placeid);
                    if (stillNeedsPosition && details?.position) {
                        newFields.lat = details?.position?.lat;
                        newFields.lng = details?.position?.lng;
                    }
                    if (stillNeedsLocality && details?.locality) {
                        newFields.locality = details?.locality;
                    }
                } else if (stillNeedsLocality) {
                    newFields.locality = await getLocalityFn(newFields.placeid);
                }
            }
        } catch (err) {
            logger.warn(`Failed in Google placeId-based address geocoding: ${err}`);
            console.warn('Failed in Google placeId-based address geocoding!', err);
        }
    }
}

type PlaceIdentifiers = {
    position: Position | undefined,
    placeid: string | undefined,
    address: string | undefined,
    docid: string | undefined,
};

// Two place records refer to the same physical place when they share a Google place id,
// the exact same coordinates, or a sufficiently specific address (see narrowAddressKey).
// We deliberately do NOT match on coarse addresses: a bare locality like "Anguilla" would
// otherwise lump together every unrelated place on the island.
function isSamePlace(a: PlaceIdentifiers, b: PlaceIdentifiers) {
    if (a.placeid && b.placeid && a.placeid === b.placeid) {
        return true;
    }
    const aPos = positionFromLatLng(a.position);
    const bPos = positionFromLatLng(b.position);
    if (aPos && bPos && positionToKey(aPos) === positionToKey(bPos)) {
        return true;
    }
    const aAddr = narrowAddressKey(a.address);
    const bAddr = narrowAddressKey(b.address);
    return !!aAddr && !!bAddr && aAddr === bAddr;
}

export function useCoincidentActivities(place: Place | undefined) {
    const placeIdentifiers = usePlaceIdentifiers(place);
    const {
        activityIdsByPlaceId,
        activityIdsByPositionKey,
        activityIdsByNarrowAddress,
        allActivities,
    } = useContext(AllTripActivitiesContext);

    const coincidentPlaceIds = useMemo(
        () => {
            const ids = new Set<string>();
            if (placeIdentifiers.placeid) {
                activityIdsByPlaceId.get(placeIdentifiers.placeid)?.forEach(docid => ids.add(docid));
            }
            const position = positionFromLatLng(placeIdentifiers.position);
            if (position) {
                activityIdsByPositionKey.get(positionToKey(position))?.forEach(docid => ids.add(docid));
            }
            const narrowAddress = narrowAddressKey(placeIdentifiers.address);
            if (narrowAddress) {
                activityIdsByNarrowAddress.get(narrowAddress)?.forEach(docid => ids.add(docid));
            }
            if (placeIdentifiers.docid) {
                ids.delete(placeIdentifiers.docid);
            }
            return ids;
        },
        [ placeIdentifiers, activityIdsByPositionKey, activityIdsByPlaceId, activityIdsByNarrowAddress ]
    );

    return useMemo(
        () => {
            const matches = ([ ...coincidentPlaceIds ]
                .map(docid => allActivities.get(docid))
                .filter(p => !!p) as Place[])
                .sort(placeReverseSorter);
            // Collapse to one row per trip: the same place is often added to a trip more than
            // once (multi-day stays, repeat visits). Keep the most recent occurrence per trip.
            const seenTrips = new Set<string>();
            return matches.filter(p => {
                const tripKey = p.tripdocid ?? p.docid ?? '';
                if (seenTrips.has(tripKey)) {
                    return false;
                }
                seenTrips.add(tripKey);
                return true;
            });
        },
        [ coincidentPlaceIds, allActivities ]
    );
}

export function usePlaceIdentifiers(place: Place | undefined) {
    return useMemo(
        () => ({
            position: positionFromLatLng({ lat: place?.lat, lng: place?.lng }),
            placeid: place?.placeid ?? undefined,
            address: place?.address ?? undefined,
            docid: place?.docid ?? undefined,
        }),
        [ place?.placeid, place?.address, place?.docid, place?.lat, place?.lng ]
    );
}

export function useCoincidentSavedPlaces(place: Place | undefined) {
    const placeIdentifiers = usePlaceIdentifiers(place);
    const { uidPlaces } = useContext(HomeContext);
    return useMemo(
        () => uidPlaces.filter(savedPlace =>
            savedPlace.docid !== placeIdentifiers.docid
            && isSamePlace(placeIdentifiers, {
                position: positionFromLatLng(savedPlace),
                placeid: savedPlace.placeid ?? undefined,
                address: savedPlace.address ?? undefined,
                docid: savedPlace.docid ?? undefined,
            })),
        [ placeIdentifiers, uidPlaces ]
    );
}
